/**
 * Normalizes Shopify Product Variant ID.
 * @param {string} id - The Shopify variant ID.
 * @returns {string} - The normalized ID.
 */
export function normalizeId(id) {
    return id.replace(/^gid:\/\/shopify\/ProductVariant\//, "");
}

/**
 * Formats price as a currency string.
 * @param {number|string|null} price - The price value.
 * @returns {string} - The formatted price string (e.g., "$199.99").
 */
export function formatPrice(price) {
    return price !== null ? `$${parseFloat(price).toFixed(2)}` : "";
}

/**
 * Formats wall height from raw metafield data.
 * @param {string} heightRaw - The raw height JSON string.
 * @returns {string} - The formatted wall height (e.g., '52"').
 */
export function formatWallHeight(heightRaw) {
    return `${parseFloat(JSON.parse(heightRaw).value)}"`;
}

/**
 * Generates the image URL for a given pool or liner name.
 * @param {string} name - The name of the item.
 * @returns {string} - The correctly formatted Shopify CDN image URL.
 */
export function generateImageURL(name) {
    return `https://cdn.shopify.com/s/files/1/0710/0602/8004/files/${name.toLowerCase().replace(/\s+/g, "").replace(/foot/gi, "")}.png`;
}

/**
 * Attaches an event listener to elements matching a selector.
 * Ensures duplicate event listeners are not attached.
 * @param {Element} container - The parent container to search within.
 * @param {string} selector - The CSS selector for target elements.
 * @param {string} event - The event type (e.g., 'click').
 * @param {Function} handler - The event handler function.
 */
export function attachEvent(container, selector, event, handler) {
    container.querySelectorAll(selector).forEach(element => {
        if (!element.dataset.listenerAttached) {
            element.addEventListener(event, handler);
            element.dataset.listenerAttached = "true";
        }
    });
}

/**
 * Sorts options by stock availability first, then by price.
 * @param {Array} options - The array of options to sort.
 * @returns {Array} - The sorted array.
 */
export function sortOptions(options) {
    return options.sort((a, b) => (b.stock - a.stock) || ((a.startingPrice || 0) - (b.startingPrice || 0)));
}

/**
 * Calculates the lowest possible starting price for a pool style or shape.
 * @param {Array} poolVariants - The available pool variants.
 * @param {Array} liners - The available liners.
 * @param {Array} compatibleTypes - The compatible liner types for the pool.
 * @param {string} poolHeight - The pool's wall height.
 * @param {number} skimmerPrice - The price of the skimmer.
 * @returns {number|null} - The lowest valid total price, or null if no valid combination exists.
 */
export function calculateStartingPrice(poolVariants, liners, compatibleTypes, poolHeight, skimmerPrice) {
    let cheapestCombination = Infinity;

    poolVariants.forEach(variant => {
        if (!variant.available) return;

        const size = variant.size;
        const cheapestLiner = getCheapestValidCombination(liners, compatibleTypes, poolHeight, size);

        if (cheapestLiner !== null) {
            const totalPrice = parseFloat(variant.price) + cheapestLiner + skimmerPrice;
            cheapestCombination = Math.min(cheapestCombination, totalPrice);
        }
    });

    return isFinite(cheapestCombination) ? cheapestCombination : null;
}

/**
 * Finds the cheapest valid liner for a given pool size and height.
 * @param {Array} liners - The available liners.
 * @param {Array} compatibleTypes - The compatible liner types for the pool.
 * @param {string} poolHeight - The pool's wall height.
 * @param {string} size - The pool's size.
 * @returns {number|null} - The lowest price for a compatible liner, or null if no valid liners exist.
 */
export function getCheapestValidCombination(liners, compatibleTypes, poolHeight, size) {
    let cheapestLinerPrice = Infinity;
    let validLinerFound = false;
    let failedLiners = [];

    const formattedPoolHeight = poolHeight.toString().replace('"', ''); // 🔥 Normalize height formatting

    liners.forEach(liner => {
        liner.variants.forEach(v => {
            const matchesType = compatibleTypes.includes(v.type);
            const matchesHeight = v.height.toString().trim() === formattedPoolHeight.trim(); // 🔥 Compare normalized heights
            const matchesSize = v.size.toLowerCase().trim() === size.toLowerCase().trim();
            const isAvailable = v.available;

            if (!matchesType || !matchesHeight || !matchesSize || !isAvailable) {
                failedLiners.push(liner.name);
            }

            if (matchesType && matchesHeight && matchesSize && isAvailable) {
                validLinerFound = true;
                cheapestLinerPrice = Math.min(cheapestLinerPrice, parseFloat(v.price));
            }
        });
    });

    return validLinerFound ? cheapestLinerPrice : null;
}

/**
 * Checks if at least one variant is available.
 * @param {Array} variants - The list of variants.
 * @returns {boolean} - True if any variant is available, otherwise false.
 */
export function isVariantAvailable(variants) {
    return variants.some(v => v.available);
}

/**
 * Determines if a liner matches the pool based on height and compatibility.
 * @param {Object} liner - The liner object.
 * @param {string} poolHeight - The pool's height.
 * @param {Array} compatibleTypes - The compatible liner types.
 * @returns {boolean} - True if the liner matches, false otherwise.
 */
export function matchLinerToPool(liner, poolHeight, compatibleTypes) {
    if (!liner || !Array.isArray(liner.variants)) {
        console.warn(`🚨 Invalid liner data:`, liner);
        return false; // Prevents the error by returning false instead of crashing
    }

    return liner.variants.some(v =>
        compatibleTypes.includes(v.type) &&
        v.height.toString() === poolHeight.toString()
    );
}

/**
 * Renders additional info for an option (e.g., pool material, wall height).
 * @param {Object} option - The option object.
 * @returns {string} - The formatted HTML content.
 */
export function renderExtraInfo(option) {
    return `
        <p class="card-text fw-bold fs-6"><strong>Material:</strong> ${option.material}</p>
        <p class="card-text fw-bold fs-6"><strong>Wall Height:</strong> ${option.height}</p>
    `;
}

/**
 * Handles user selection for a given step and updates the configurator state.
 * @param {Object} configurator - The main configurator instance.
 * @param {string} stepKey - The step name (e.g., "pool-style").
 * @param {Object} selectedItem - The selected item object.
 */
/**
 * Handles user selection for a given step and updates the configurator state.
 * @param {Object} configurator - The main configurator instance.
 * @param {string} stepKey - The step name (e.g., "pool-style", "liner").
 * @param {Object} selectedItem - The selected item object.
 */
export function handleSelection(configurator, stepKey, selectedItem) {

    if (selectedItem === null) {
        // ✅ Handle "No Equipment" or deselection cases properly
        if (stepKey === "equipment") {
            configurator.state.selectedEquipment = null;
        }
        configurator.handleSelection(stepKey);
        return;
    }

    // ✅ Ensure nested selections update correctly
    if (stepKey === "pool-style") {
        configurator.state.selectedPool = { ...selectedItem, variants: [...selectedItem.variants] };
    }
    if (stepKey === "pool-shape") {
        configurator.state.selectedPool.shape = selectedItem;
    }
    if (stepKey === "pool-size") {
        configurator.state.selectedPool.size = selectedItem;
        configurator.state.selectedPool.price = selectedItem.price.toFixed(2);
    }
    if (stepKey === "liner") {
        configurator.state.selectedLiner = {
            ...selectedItem,
            price: parseFloat(selectedItem.price) || 0, // ✅ Ensure price is stored correctly
            size: selectedItem.size || "Unknown",
            type: selectedItem.type || "Unknown"
        };
    }
    if (stepKey === "equipment") {
        configurator.state.selectedEquipment = {
            ...selectedItem,
            filter: {
                ...selectedItem.filter,
                price: parseFloat(selectedItem.filter.variants[0].price) || 0
            },
            pump: {
                ...selectedItem.pump,
                price: parseFloat(selectedItem.pump.variants[0].price) || 0
            }
        };
    }

    configurator.handleSelection(stepKey);
}
